import {
	useUpdateContactProspectMutation,
	useContactSearchForEmailsMutation,
	useContactSearchForPhonesMutation,
} from "@/pages/Private/redux/contact/contact.api";
import {
	useUpdateProspectMutation,
	useSearchForEmailsMutation,
	useSearchForPhonesMutation,
} from "@/pages/Private/redux/list/list.api";

import { translateRequestError } from "@/utils/errors";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { ContactDomain } from "./types";
import { ContactInfo } from "./schema";

interface ContactChannelActions {
	update: (values: ContactInfo) => Promise<unknown>;
	searchEmail: (id: number) => Promise<unknown>;
	searchPhone: (id: number) => Promise<unknown>;
	isUpdating: boolean;
	isSearchingEmail: boolean;
	isSearchingPhone: boolean;
}

/**
 * Domain-aware bridge to the right update + enrichment endpoints. Both domains'
 * hooks are always called (React's rules of hooks) and the relevant trigger is
 * picked by `domain` at call time — this is what fixes the previous `LeadName`
 * bug that searched phones through the contact endpoint on list rows.
 */
/**
 * A lookup the API refuses (402 "not enough credits" since PREC-4437) says why. Its callers fire
 * it from an icon click and do not await it, so a rejection here would otherwise be unseen.
 */
const sayWhyIfRefused = (request: Promise<unknown>): Promise<unknown> =>
	request.catch((error: unknown) => {
		showGlobalToast(translateRequestError(error));
	});

export const useContactChannelActions = (domain: ContactDomain): ContactChannelActions => {
	const [updateContact, contactUpdateState] = useUpdateContactProspectMutation();
	const [updateList, listUpdateState] = useUpdateProspectMutation();
	const [contactSearchEmail, contactEmailState] = useContactSearchForEmailsMutation();
	const [listSearchEmail, listEmailState] = useSearchForEmailsMutation();
	const [contactSearchPhone, contactPhoneState] = useContactSearchForPhonesMutation();
	const [listSearchPhone, listPhoneState] = useSearchForPhonesMutation();

	const isContact = domain === "contact";

	const update = (values: ContactInfo) =>
		isContact ? updateContact(values).unwrap() : updateList(values).unwrap();

	const searchEmail = (id: number) =>
		sayWhyIfRefused(
			isContact
				? contactSearchEmail({ ids: [id] }).unwrap()
				: listSearchEmail({ ids: [id] }).unwrap()
		);

	const searchPhone = (id: number) =>
		sayWhyIfRefused(
			isContact
				? contactSearchPhone({ ids: [id] }).unwrap()
				: listSearchPhone({ ids: [id] }).unwrap()
		);

	return {
		update,
		searchEmail,
		searchPhone,
		isUpdating: isContact ? contactUpdateState.isLoading : listUpdateState.isLoading,
		isSearchingEmail: isContact ? contactEmailState.isLoading : listEmailState.isLoading,
		isSearchingPhone: isContact ? contactPhoneState.isLoading : listPhoneState.isLoading,
	};
};
